import React, { useState } from 'react';
import { Grid3x3 } from 'lucide-react';

const LEVELS = ['overview', 'level_1', 'level_2', 'level_3', 'level_4', 'level_5'];
const LEVEL_LABELS = {
  overview: 'Overview',
  level_1: 'Level 1',
  level_2: 'Level 2',
  level_3: 'Level 3',
  level_4: 'Level 4',
  level_5: 'Level 5',
};

const getCellStyle = (cell) => {
  if (!cell?.hasVideo) {
    return {
      bg: 'bg-white/5',
      text: 'text-white/20',
      border: 'border-white/5',
      label: '—',
    };
  }
  const rate = cell?.completionRate ?? 0;
  if (cell?.totalViews === 0) {
    return {
      bg: 'bg-purple-900/40',
      text: 'text-purple-400',
      border: 'border-purple-700/30',
      label: 'No views',
    };
  }
  if (rate >= 80) return { bg: 'bg-emerald-500/70', text: 'text-white', border: 'border-emerald-400/50', label: `${rate}%` };
  if (rate >= 60) return { bg: 'bg-emerald-500/40', text: 'text-emerald-200', border: 'border-emerald-500/30', label: `${rate}%` };
  if (rate >= 40) return { bg: 'bg-amber-500/40', text: 'text-amber-200', border: 'border-amber-500/30', label: `${rate}%` };
  if (rate >= 20) return { bg: 'bg-orange-500/40', text: 'text-orange-200', border: 'border-orange-500/30', label: `${rate}%` };
  return { bg: 'bg-red-500/30', text: 'text-red-300', border: 'border-red-500/30', label: `${rate}%` };
};

const HeatMapGrid = ({ data, loading }) => {
  const [tooltip, setTooltip] = useState(null);
  const [search, setSearch] = useState('');

  if (loading) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
        <div className="h-5 bg-white/20 rounded animate-pulse w-56 mb-6" />
        <div className="space-y-2">
          {[1, 2, 3, 4, 5]?.map(i => (
            <div key={i} className="h-10 bg-white/10 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const filtered = (data || [])?.filter(row =>
    !search || row?.songTitle?.toLowerCase()?.includes(search?.toLowerCase())
  );

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Grid3x3 className="w-4 h-4 text-purple-300" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">Completion Rate Heat Map</h2>
            <p className="text-purple-300 text-xs mt-0.5">
              {filtered?.length} song{filtered?.length !== 1 ? 's' : ''} · color intensity = completion rate
            </p>
          </div>
        </div>
        <input
          type="text"
          placeholder="Search songs..."
          value={search}
          onChange={e => setSearch(e?.target?.value)}
          className="bg-white/10 border border-white/20 rounded-xl px-3 py-1.5 text-white text-sm placeholder-purple-400 focus:outline-none focus:border-purple-400 w-full sm:w-48"
        />
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <span className="text-purple-400 text-xs font-medium">Completion:</span>
        {[
          { bg: 'bg-white/5', label: 'No video' },
          { bg: 'bg-purple-900/40', label: '0 views' },
          { bg: 'bg-red-500/30', label: '<20%' },
          { bg: 'bg-orange-500/40', label: '20–40%' },
          { bg: 'bg-amber-500/40', label: '40–60%' },
          { bg: 'bg-emerald-500/40', label: '60–80%' },
          { bg: 'bg-emerald-500/70', label: '80%+' },
        ]?.map(item => (
          <div key={item?.label} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded ${item?.bg} border border-white/10`} />
            <span className="text-purple-300 text-xs">{item?.label}</span>
          </div>
        ))}
      </div>

      {filtered?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-purple-300">
          <p className="text-lg font-medium">No songs found</p>
          <p className="text-sm mt-1">
            {search ? 'Try a different search term' : 'No published videos yet'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr>
                <th className="text-left py-2 px-3 text-purple-300 text-xs font-semibold uppercase tracking-wide w-48">
                  Song
                </th>
                {LEVELS?.map(lvl => (
                  <th key={lvl} className="text-center py-2 px-2 text-purple-300 text-xs font-semibold uppercase tracking-wide">
                    {LEVEL_LABELS?.[lvl]}
                  </th>
                ))}
                <th className="text-center py-2 px-2 text-purple-300 text-xs font-semibold uppercase tracking-wide">
                  Avg
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered?.map((row, i) => {
                const cellsWithViews = LEVELS?.filter(lvl => row?.levels?.[lvl]?.totalViews > 0);
                const avgRate = cellsWithViews?.length > 0
                  ? Math.round(cellsWithViews?.reduce((s, lvl) => s + (row?.levels?.[lvl]?.completionRate || 0), 0) / cellsWithViews?.length)
                  : null;

                return (
                  <tr key={row?.songId || i} className="border-t border-white/5 hover:bg-white/5 transition-colors group">
                    <td className="py-2 px-3">
                      <p className="text-white text-sm font-medium truncate max-w-[180px] group-hover:text-purple-200 transition-colors">
                        {row?.songTitle}
                      </p>
                    </td>
                    {LEVELS?.map(lvl => {
                      const cell = row?.levels?.[lvl];
                      const style = getCellStyle(cell);
                      return (
                        <td key={lvl} className="py-2 px-2 text-center">
                          <div
                            className={`relative mx-auto w-14 h-9 rounded-lg border ${style?.bg} ${style?.border} flex items-center justify-center cursor-default transition-all hover:scale-105`}
                            onMouseEnter={() => setTooltip({ songTitle: row?.songTitle, level: LEVEL_LABELS?.[lvl], cell })}
                            onMouseLeave={() => setTooltip(null)}
                          >
                            <span className={`text-xs font-semibold ${style?.text}`}>
                              {style?.label}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                    <td className="py-2 px-2 text-center">
                      {avgRate !== null ? (
                        <span className={`text-sm font-bold ${avgRate >= 60 ? 'text-emerald-300' : avgRate >= 40 ? 'text-amber-300' : 'text-red-300'}`}>
                          {avgRate}%
                        </span>
                      ) : (
                        <span className="text-purple-500 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Tooltip */}
      {tooltip && (
        <div className="fixed bottom-6 right-6 bg-gray-900 border border-purple-500/40 rounded-xl p-4 shadow-2xl z-50 min-w-[200px] pointer-events-none">
          <p className="text-white font-semibold text-sm mb-1">{tooltip?.songTitle}</p>
          <p className="text-purple-300 text-xs mb-2">{tooltip?.level}</p>
          {tooltip?.cell?.hasVideo ? (
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-purple-400">Completion Rate</span>
                <span className="text-white font-bold">{tooltip?.cell?.completionRate ?? 0}%</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-purple-400">Total Views</span>
                <span className="text-white">{tooltip?.cell?.totalViews ?? 0}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-purple-400">Avg Watch</span>
                <span className="text-white">{tooltip?.cell?.avgWatchPct ?? 0}%</span>
              </div>
            </div>
          ) : (
            <p className="text-purple-400 text-xs">No video uploaded for this level</p>
          )}
        </div>
      )}
    </div>
  );
};

export default HeatMapGrid;
